import { HttpErrorResponse } from '@angular/common/http';
import { Component, Inject, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Constants } from 'src/app/app.constants';
import { Reward, StampCard, Voucher } from 'src/app/models';
import { CardService, RewardService, VoucherService } from 'src/app/services';

interface FilterOptions {
  label: string;
  key: string;
  followOn?: {
    key: string;
    label: string;
    formControl: FormControl;
  };
}

interface VoucherStatusOption {
  label: string;
  value: string;
  description: string;
}

@Component({
  selector: 'recipients-filter-dialog',
  templateUrl: './recipients-filter-dialog.component.html',
  styleUrls: ['./recipients-filter-dialog.component.css'],
})
export class RecipientsFilterDialog implements OnInit {
  filterOptions: FilterOptions[] = [];

  filters: any = {};
  hasFilters = false;
  cardsLoading = false;
  cards: StampCard[] = [];
  vouchersLoading = false;
  vouchers: Voucher[] = [];
  rewardsLoading = false;
  rewards: Reward[] = [];
  voucherStatusOptions: VoucherStatusOption[] = [];

  form!: FormGroup;
  cardsFormControl: FormControl = new FormControl();
  vouchersFormControl: FormControl = new FormControl();
  voucherStatusFormControl: FormControl = new FormControl();
  specificRewardFormControl: FormControl = new FormControl();

  constructor(
    private cardService: CardService,
    public matDialogRef: MatDialogRef<RecipientsFilterDialog>,
    public fb: FormBuilder,
    private constants: Constants,
    private voucherService: VoucherService,
    private rewardService: RewardService,

    @Inject(MAT_DIALOG_DATA) public data: any,
  ) {
    this.filters = this.cloneFilters(data?.filters);

    this.hasFilters = this.getHasFilters();

    this.form = this.fb.group({
      cards: this.cardsFormControl,
      vouchers: this.vouchersFormControl,
      voucherStatus: this.voucherStatusFormControl,
      specificReward: this.specificRewardFormControl,
    });
  }

  ngOnInit(): void {
    const merchantUsesVouchers = this.data?.merchantUsesVouchers !== false;
    if (!merchantUsesVouchers) {
      this.filters['hasVoucher'] = false;
    }
    this.filterOptions = [
      {
        label: 'Upcoming birthday this month',
        key: 'birthdayThisMonth',
      },
      { label: 'Has never received a reward', key: 'receivedReward' },
      {
        label: 'Has not received a specific reward in x+ months',
        key: 'specificRewardNotReceived',
        followOn: {
          label: 'Number of months',
          key: 'specificRewardNotReceivedMonths',
          formControl: new FormControl(),
        },
      },
      { label: 'Has never redeemed a reward', key: 'redeemedReward' },
      {
        label: 'Has never received a stamp or points',
        key: 'receivedStamp',
      },
      {
        label: 'Has never received a push notification',
        key: 'receivedNotification',
      },
      {
        label: 'Has un-redeemed rewards',
        key: 'unredeemedRewards',
      },
      {
        label: 'Has not received a stamp or points in x+ days',
        key: 'lapsedStamps',
        followOn: {
          label: 'Number of days',
          key: 'lapsedStampsDays',
          formControl: new FormControl(),
        },
      },
      {
        label: 'Has at least one voucher',
        key: 'hasVoucher',
      },
    ].filter((option) => option.key !== 'hasVoucher' || merchantUsesVouchers);

    this.voucherStatusOptions = [
      {
        label: 'Active',
        value: 'active',
        description: 'Voucher is valid and not yet redeemed',
      },
      {
        label: 'Pending',
        value: 'pending',
        description: 'Voucher has not yet become active',
      },
      {
        label: 'Expired',
        value: 'expired',
        description: 'Voucher has expired',
      },
      {
        label: 'Redeemed',
        value: 'redeemed',
        description: 'Voucher has been fully redeemed',
      },
      {
        label: 'Cancelled',
        value: 'cancelled',
        description: 'Voucher has been cancelled',
      },
      {
        label: 'Partial',
        value: 'partial',
        description: 'Voucher has been partially redeemed',
      },
    ];

    this.getCards();
    this.getVouchers();
    this.getRewards();

    this.filterOptions.forEach((option) => {
      if (option.followOn && this.filters[option.followOn.key]) {
        option.followOn.formControl.setValue(this.filters[option.followOn.key]);
      }
    });
  }

  getCards(): void {
    this.cardsLoading = true;
    this.cardService
      .getCards(100, 0)
      .subscribe({
        next: (res: any) => {
          if (res) {
            this.cards = res.docs;

            const titleCounts = this.cards.reduce(
              (acc, card) => {
                if (card.title) {
                  acc[card.title] = (acc[card.title] || 0) + 1;
                }
                return acc;
              },
              {} as Record<string, number>,
            );

            this.cards.forEach((el) => {
              if (el.title && titleCounts[el.title] > 1) {
                el.title = `${el.title} (#${el.ref})`;
              }
            });

            if (Array.isArray(this.data?.cards) && this.data.cards.length > 0) {
              this.cardsFormControl.setValue(
                this.cards.filter((el) => this.data.cards.includes(el._id)),
              );
            } else {
              this.cardsFormControl.setValue(this.cards);
            }
          }
        },
        error: (res: HttpErrorResponse) => {
          this.constants.snack(res.error.message);
        },
      })
      .add(() => (this.cardsLoading = false));
  }

  getVouchers(): void {
    this.vouchersLoading = true;
    this.voucherService
      .getVouchers(100, 0, true)
      .subscribe({
        next: (res: any) => {
          if (res) {
            this.vouchers = res.docs.map((el: Voucher) => {
              return {
                ...el,
                displayTitle: el.displayAs,
              };
            });

            if (
              Array.isArray(this.data?.vouchers) &&
              this.data.vouchers.length > 0
            ) {
              this.vouchersFormControl.setValue(
                this.vouchers.filter((el) =>
                  this.data.vouchers.includes(el._id),
                ),
              );
            } else {
              this.vouchersFormControl.setValue([]);
            }

            if (
              Array.isArray(this.data?.voucherStatus) &&
              this.data.voucherStatus.length > 0
            ) {
              this.voucherStatusFormControl.setValue(
                this.voucherStatusOptions.filter((el) =>
                  this.data.voucherStatus.includes(el.value),
                ),
              );
            } else {
              this.voucherStatusFormControl.setValue(
                this.voucherStatusOptions.filter((el) =>
                  ['active', 'partial'].includes(el.value),
                ),
              );
            }
          }
        },
        error: (res: HttpErrorResponse) => {
          this.constants.snack(res.error.message);
        },
      })
      .add(() => (this.vouchersLoading = false));
  }

  getRewards(): void {
    this.rewardsLoading = true;
    this.rewardService
      .getRewards(250, 0)
      .subscribe({
        next: (res: any) => {
          if (res) {
            this.rewards = res.docs ?? [];
            const selectedRewardId =
              this.filters['specificRewardNotReceivedReward'];
            if (selectedRewardId) {
              const selectedReward = this.rewards.find(
                (reward) =>
                  reward._id?.toString() === selectedRewardId?.toString(),
              );
              this.specificRewardFormControl.setValue(selectedReward || null);
            }
          }
        },
        error: (res: HttpErrorResponse) => {
          this.constants.snack(res.error.message);
        },
      })
      .add(() => (this.rewardsLoading = false));
  }

  toggle(key: string, value: any): void {
    this.filters[key] = value;

    if (key === 'specificRewardNotReceived' && !value) {
      this.filters['specificRewardNotReceivedMonths'] = undefined;
      this.filters['specificRewardNotReceivedReward'] = undefined;
      this.filterOptions.forEach((option) => {
        if (option.key === 'specificRewardNotReceived' && option.followOn) {
          option.followOn.formControl.setValue(null);
        }
      });
      this.specificRewardFormControl.setValue(null);
    }

    this.hasFilters = this.getHasFilters();
  }

  primaryTapped() {
    let canProceed = true;

    this.filterOptions.forEach((filterOption) => {
      if (filterOption.followOn) {
        const mainFilter = this.filters[filterOption.key];
        const followOnFilter = this.filters[filterOption.followOn.key];

        if (mainFilter && !followOnFilter) {
          canProceed = false;
        }
      }
    });

    if (
      this.filters['specificRewardNotReceived'] &&
      !this.specificRewardFormControl.value?._id
    ) {
      this.constants.snack('Please select a reward');
      return;
    }

    if (this.filters['hasCard'] && !this.cardsFormControl.value?.length) {
      this.constants.snack('Please select at least one card');
      return;
    }

    if (this.filters['hasVoucher']) {
      if (!this.vouchersFormControl.value?.length) {
        this.constants.snack('Please select at least one voucher');
        return;
      }
      if (!this.voucherStatusFormControl.value?.length) {
        this.constants.snack('Please select at least one voucher status');
        return;
      }
    }

    if (!canProceed) {
      this.constants.snack('Please complete all fields');
    } else {
      this.filters['specificRewardNotReceivedReward'] =
        this.specificRewardFormControl.value?._id;
      this.matDialogRef.close({
        filters: this.filters,
        cards: this.filters['hasCard']
          ? this.cardsFormControl.value.map((el: StampCard) => el._id)
          : [],
        vouchers: this.vouchersFormControl.value.map((el: Voucher) => el._id),
        voucherStatus: this.voucherStatusFormControl.value.map(
          (el: VoucherStatusOption) => el.value,
        ),
      });
    }
  }

  secondaryTapped() {
    Object.keys(this.filters).forEach((el: any) => {
      this.filters[el] = false;
    });

    this.cardsFormControl.setValue(this.cards);
    this.vouchersFormControl.setValue([]);
    this.voucherStatusFormControl.setValue([]);
    this.specificRewardFormControl.setValue(null);
  }

  private cloneFilters(originalFilters: any): any {
    return originalFilters ? JSON.parse(JSON.stringify(originalFilters)) : {};
  }

  getHasFilters(): boolean {
    var filtersApplied = false;

    Object.keys(this.filters).forEach((el: any) => {
      if (this.filters[el] === true) {
        filtersApplied = true;
      }
    });

    return filtersApplied;
  }
}
